/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable no-prototype-builtins */
import angular from 'angular';
import moment from 'moment';
import { ConfigService, EUserSettings } from '../config/config.service';

/* @ngInject */
export class TableService {
  private configService: ConfigService;

  constructor(
    configService: ConfigService,
  ) {
    'ngInject';

    this.configService = configService;
  }

  /**
   * Export the provided dataset to a csv file.
   * @param removeKeys delete keys from object
   * @param sortKeys sort these keys to the front of the table
   * @param translationJson translate object keys
   */
  exportDataset<T>(
    dataset: T[],
    fileName: string,
    removeKeys?: Array<keyof T>,
    sortKeys?: Array<keyof T>,
    translationJson?: { [key in keyof T]: {
      translation: string,
      type: string,
    } },
  ): void {
    let data: T[] = JSON.parse(JSON.stringify(dataset));

    if (Array.isArray(removeKeys) && removeKeys) {
      data.forEach((d) => {
        removeKeys.forEach((key) => {
          delete d[key];
        });
      });
    }

    if (Array.isArray(sortKeys) && sortKeys) {
      const sortedArray: T[] = [];

      data.forEach((d) => {
        let object = d;

        sortKeys.reverse().forEach((key) => {
          const tempObject: any = {};
          tempObject[key] = d[key];

          delete object[key];
          object = Object.assign(tempObject, d);
        });

        sortedArray.push(object);
      });

      data = sortedArray;
    }

    const header: string[] = Object.keys(data[0]);
    let headerTranslation = header;

    if (translationJson !== undefined) {
      headerTranslation = headerTranslation.map((heading) => {
        if (translationJson.hasOwnProperty(heading)) {
          return translationJson[heading as keyof T].translation;
        }

        return heading;
      });

      // Convert values if data type is defined in translation json
      for (const row of data) {
        for (const key in row) {
          if (translationJson.hasOwnProperty(key)) {
            row[key] = this.convertValue(row[key], translationJson[key].type);
          }
        }
      }
    }

    // Convert Object to CSV Values
    const CSV_DELIMITER = this.configService.getUserConfig(EUserSettings.CSV_COMPATABILITY) ? ';' : ',';
    const replacer = (_key: string, value: any) => (value === null ? '' : value); // specify how null values are handled.
    const csv = data.map((row) => header
      .map((fieldName) => JSON.stringify(row[fieldName as keyof T], replacer))
      .join(CSV_DELIMITER));

    csv.unshift(headerTranslation.join(CSV_DELIMITER));
    const resultCsv = csv.join('\r\n');

    this.download(`${fileName}-${moment().format('YYYY-MM-DD-hh-mm')}.csv`, resultCsv);
  }

  download(
    filename: string,
    text: string,
  ): void {
    const pom = document.createElement('a');
    pom.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`);
    pom.setAttribute('download', filename);

    if (document.createEvent) {
      const event = document.createEvent('MouseEvents');
      event.initEvent('click', true, true);
      pom.dispatchEvent(event);
    } else {
      pom.click();
    }
  }

  /**
   * Convert the supplied value to correct string format.
   *
   * @param {any} value
   * @param {string} type
   * @returns {string}
   */
  convertValue(
    value: any,
    type: string,
  ): any {
    if (value === null) return '-';

    switch (type) {
      case 'date':
        return moment(value).format('DD.MM.YYYY');
      case 'float':
        return String(value).replace('.', ',');
      case 'boolean':
        return value ? 'Ja' : 'Nein';
      case 'percentage':
        return `${String(value as number * 100)}%`;
      case 'option_code':
        return (value as any[]).map((o) => o.option_desc.replace('"', '').replace('/', ''));
      default:
        return value as string;
    }
  }
}

'ngInject';
export default angular.module('services.table', [])
  .service('tableService', TableService)
  .name;
